
import { BatchWriteItemCommand, AttributeValue, QueryCommand, ScanCommand } from "@aws-sdk/client-dynamodb";
import { AUDIT_LOG_TABLE } from "@constants/dynamoDBTableName";
import { dbClient } from "@utils/awsClients";
import { formatDateDMY, getDataUsingUserId } from "@utils/sharedUtils";
import { v4 as uuidv4 } from 'uuid';

export interface AuditLog {
  uuid: string;
  action: string;
  model_id: string;
  model_type: string;
  user_id: string;
  properties: Record<string, any>;
  host: string;
  timestamp: string;
  time?: string;
}

const logBuffer: AuditLog[] = [];
const BUFFER_SIZE = 25;
let bufferTimeout: NodeJS.Timeout | null = null;

// Function to convert AuditLog to DynamoDB format
const convertToDynamoDBItem = (log: AuditLog): Record<string, AttributeValue> => {
  return {
    uuid: { S: log.uuid },
    action: { S: log.action },
    model_id: { S: log.model_id },
    model_type: { S: log.model_type },
    user_id: { S: log.user_id },
    properties: { S: JSON.stringify(log.properties) }, // Assuming properties is an object, convert to string
    host: { S: log.host },
    timestamp: { S: log.timestamp },
  };
};

const flushBuffer = async () => {
  if (logBuffer.length === 0) return;

  const batch = logBuffer.splice(0, BUFFER_SIZE);
  const command = new BatchWriteItemCommand({
    RequestItems: {
      [AUDIT_LOG_TABLE!]: batch.map(item => ({
        PutRequest: { Item: convertToDynamoDBItem(item) }
      }))
    }
  });

  try {
    await dbClient.send(command);
  } catch (error) {
    console.error("Error flushing buffer:", error);
  }
};

export const addAuditLog = async (log: Omit<AuditLog, 'uuid' | 'timestamp'>): Promise<void> => {
  const newLog: AuditLog = {
    uuid: uuidv4(),
    timestamp: new Date().toISOString(),
    ...log
  };

  logBuffer.push(newLog);

  if (logBuffer.length >= BUFFER_SIZE) {
    await flushBuffer();
  } else if (!bufferTimeout) {
    bufferTimeout = setTimeout(() => {
      flushBuffer();
      bufferTimeout = null;
    }, 5000);
  }
};

export const getAuditLogs = async (lastEvaluatedKey?: any): Promise<{ logs: AuditLog[], lastEvaluatedKey: any }> => {
  const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const command = new ScanCommand({
    TableName: AUDIT_LOG_TABLE,
    Limit: 10,
    ExclusiveStartKey: lastEvaluatedKey,
  });

  try {
    const response = await dbClient.send(command);
    if (response.Items) {
      const auditLogs = await Promise.all(
        response.Items.map(async (item) => {
          const userIdVal = item.user_id?.S || '';
          const res = await getDataUsingUserId('user_info', userIdVal);

          if (!res || res?.Items?.length === 0) {
            return null;
          }

          const details = res?.Items ? res.Items[0] : null;
          const createdAt = item.timestamp?.S || '';
          if (!createdAt) return null;
          const dateObj = new Date(createdAt);

          const date = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
          let hours = dateObj.getHours();
          const minutes = dateObj.getMinutes().toString().padStart(2, '0');
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          const time = `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;

          let parsedProperties = {};
          try {
            parsedProperties = item.properties?.S ? JSON.parse(item.properties.S) : {};
          } catch (e) {
            console.error("Error parsing audit log properties:", e);
          }

          return {
            uuid: item.uuid?.S || '',
            action: item.action?.S || '',
            host: item.host?.S || '',
            model_id: item.model_id?.S || '',
            model_type: item.model_type?.S || '',
            user_id: details?.name?.S ? details?.name?.S : "--",
            properties: parsedProperties,
            timestamp: formatDateDMY(createdAt),
            time,
          } as AuditLog;
        })
      );

      const validLogs = auditLogs.filter((log): log is AuditLog => log !== null);
      return { logs: validLogs, lastEvaluatedKey: response.LastEvaluatedKey };
    }
    return { logs: [], lastEvaluatedKey: undefined };
  } catch (error) {
    console.error('Error fetching audit logs:', error);
    return { logs: [], lastEvaluatedKey: undefined };
  }
}